import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {PaymentFormValues} from 'components/PaymentForm/initialFormValues'

import {BookingStatusLink} from '@vio/core/src/api-types/bovio/response/booking_status'

/**
 * State machine stages for booking creation flow.
 */
export enum BookingCreationStage {
  Idle = 'idle',
  Creating = 'creating',
  Awaiting3ds = 'awaiting_3ds',
  Finalizing = 'finalizing',
  Redirecting = 'redirecting',
  Confirmed = 'confirmed',
  Error = 'error'
}

/**
 * Values from React hooks set at booking submission, before the creation
 * mutation fires. Consumed by the payment redirect thunk, and by the listener
 * to seed the charge-later flag on the booking-creation state machine.
 */
export interface RedirectContext {
  paymentMethodType?: string
  customer?: PaymentFormValues['customer']
  isChargeLater?: boolean
}

/**
 * Context for the 3DS flow. Initialized at booking creation with the
 * charge-later flag, then enriched with booking status links when 3DS
 * is detected. Consumed during finalization.
 */
export interface ThreeDSContext {
  isChargeLaterBookingCreation: boolean
  bookingStatusLinks: BookingStatusLink[]
}

export interface BookingCreationState {
  stage: BookingCreationStage
  redirectContext: RedirectContext | null
  threeDSContext: ThreeDSContext | null
}

export const initialState: BookingCreationState = {
  stage: BookingCreationStage.Idle,
  redirectContext: null,
  threeDSContext: null
}

const {actions, reducer} = createSlice({
  name: 'bookingCreation',
  initialState: initialState as BookingCreationState,
  reducers: {
    /**
     * Store analytics values for the redirect thunk.
     */
    setRedirectContext: (state, action: PayloadAction<RedirectContext>) => {
      state.redirectContext = action.payload
    },

    /**
     * Transition to creating. Initializes threeDSContext with the
     * charge-later flag so it is available if 3DS is required later.
     */
    startCreating: (state, action: PayloadAction<{isChargeLater: boolean}>) => {
      state.stage = BookingCreationStage.Creating
      state.threeDSContext = {
        isChargeLaterBookingCreation: action.payload.isChargeLater,
        bookingStatusLinks: []
      }
    },

    /**
     * Transition to awaiting_3ds. Stores booking status links for finalization
     * on the existing threeDSContext.
     */
    require3DS: (
      state,
      action: PayloadAction<{bookingStatusLinks: BookingStatusLink[]}>
    ) => {
      state.stage = BookingCreationStage.Awaiting3ds
      state.threeDSContext = {
        isChargeLaterBookingCreation:
          state.threeDSContext?.isChargeLaterBookingCreation ?? false,
        bookingStatusLinks: action.payload.bookingStatusLinks
      }
    },

    /**
     * Transition to finalizing
     */
    startFinalization: state => {
      state.stage = BookingCreationStage.Finalizing
    },

    /**
     * Transition to confirmed
     */
    confirm: state => {
      state.stage = BookingCreationStage.Confirmed
    },

    /**
     * Transition to redirecting
     */
    redirect: state => {
      state.stage = BookingCreationStage.Redirecting
    },

    /**
     * Transition to error
     */
    setError: state => {
      state.stage = BookingCreationStage.Error
    },

    /**
     * Reset to idle state.
     */
    reset: () => initialState
  }
})

export const {
  setRedirectContext,
  startCreating,
  require3DS,
  startFinalization,
  confirm,
  redirect,
  setError,
  reset
} = actions

export default reducer
